import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import AdminBar from '../components/AdminBar';
import { useThemeMode } from '../components/ThemeModeContext';
import { serverDomain } from './config';

const ANY_USER_VALUE = 'any';
const ANY_MODEL_VALUE = 'any';

const formatInteger = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0';
  return number.toLocaleString('en-US');
};

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '$0.000000';
  return `$${number.toFixed(6)}`;
};

const getFriendlyError = (message, detail) => {
  if (!message) return 'Unable to load token cost data right now.';
  if (message === 'Unauthorised - Access Denied') return message;
  if (message === 'Invalid or expired token!') return 'Your session has expired. Please log in again.';
  if (detail) return `${message} ${detail}`;
  return message;
};

export default function TokenCost() {
  const { isDarkMode } = useThemeMode();
  const [isMobileViewport, setIsMobileViewport] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [adminBarHeight, setAdminBarHeight] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 56 : 60));
  const headerBg = isDarkMode ? '#000000' : '#003366';
  const headerText = '#ffffff';
  const pageBg = isDarkMode ? '#1a1a1a' : '#ffffff';
  const pageText = isDarkMode ? '#f5f5f5' : '#000000';
  const cardBg = isDarkMode ? '#222222' : '#f7f7f7';
  const borderColor = isDarkMode ? '#3a3a3a' : '#d9d9d9';
  const inputBg = isDarkMode ? '#111111' : '#ffffff';
  const controlWidth = 320;
  const stickyHeaderTop = adminBarHeight;
  const stickyHeaderBg = isDarkMode ? '#181818' : '#eef4fb';
  const appNameColumnWidth = isMobileViewport ? 160 : 200;
  const headerCellPadding = '9px 16px';

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(ANY_USER_VALUE);
  const [selectedModelType, setSelectedModelType] = useState(ANY_MODEL_VALUE);
  const [selectedModelPricing, setSelectedModelPricing] = useState(null);
  const [isTableHeaderStuck, setIsTableHeaderStuck] = useState(false);
  const stickyHeaderContainerRef = useRef(null);
  const tableHeaderViewportRef = useRef(null);
  const tableBodyViewportRef = useRef(null);

  const isAdmin = localStorage.getItem('appstar_admin') === 'true';
  const token = localStorage.getItem('appstar_jwt');

  const loadUsers = React.useCallback(() => {
    if (!token || !isAdmin) {
      setUsers([]);
      return Promise.resolve();
    }

    return axios
      .get(serverDomain + 'get-credit-values.php', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((response) => {
        const data = response?.data || {};
        if (data.success === 1) {
          setUsers(Array.isArray(data.users) ? data.users : []);
          return;
        }

        throw new Error(getFriendlyError(data.message, data.error));
      });
  }, [isAdmin, token]);

  const loadTokenCosts = React.useCallback((userId, modelType) => {
    if (!token || !isAdmin) {
      setRows([]);
      setModels([]);
      setSelectedModelPricing(null);
      setErrorMessage('Unauthorised - Access Denied');
      setLoading(false);
      return Promise.resolve();
    }

    setLoading(true);
    setErrorMessage('');

    const params = {};
    if (userId && userId !== ANY_USER_VALUE) {
      params.user_id = userId;
    }
    if (modelType && modelType !== ANY_MODEL_VALUE) {
      params.model_type = modelType;
    }

    return axios
      .get(serverDomain + 'get-token-costs.php', {
        params,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((response) => {
        const data = response?.data || {};
        if (data.success === 1) {
          setRows(Array.isArray(data.apps) ? data.apps : []);
          setModels(Array.isArray(data.model_options) ? data.model_options : []);
          setSelectedModelPricing(data.selected_model_pricing || null);
          setErrorMessage('');
          return;
        }

        setRows([]);
        setSelectedModelPricing(null);
        setErrorMessage(getFriendlyError(data.message, data.error));
      })
      .catch((error) => {
        const data = error?.response?.data || {};
        setRows([]);
        setSelectedModelPricing(null);
        setErrorMessage(getFriendlyError(data.message || error.message, data.error));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAdmin, token]);

  useEffect(() => {
    const updateViewport = () => setIsMobileViewport(window.innerWidth < 768);
    updateViewport();
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  useEffect(() => {
    const updateAdminBarHeight = () => {
      const adminBar = document.querySelector('.admin-bar');
      if (!adminBar) {
        setAdminBarHeight(window.innerWidth < 768 ? 56 : 60);
        return;
      }

      const nextHeight = Math.ceil(adminBar.getBoundingClientRect().height);
      setAdminBarHeight(nextHeight > 0 ? nextHeight : (window.innerWidth < 768 ? 56 : 60));
    };

    updateAdminBarHeight();
    window.addEventListener('resize', updateAdminBarHeight);
    window.addEventListener('orientationchange', updateAdminBarHeight);

    return () => {
      window.removeEventListener('resize', updateAdminBarHeight);
      window.removeEventListener('orientationchange', updateAdminBarHeight);
    };
  }, []);

  useEffect(() => {
    if (!token || !isAdmin) {
      setRows([]);
      setUsers([]);
      setModels([]);
      setSelectedModelPricing(null);
      setErrorMessage('Unauthorised - Access Denied');
      setLoading(false);
      return;
    }

    Promise.all([
      loadUsers(),
      loadTokenCosts(ANY_USER_VALUE, ANY_MODEL_VALUE)
    ]).catch((error) => {
      const message = error instanceof Error ? error.message : 'Unable to load token cost data right now.';
      setErrorMessage(message);
      setLoading(false);
    });
  }, [isAdmin, token]);

  useEffect(() => {
    const updateStickyHeaderState = () => {
      const stickyHeader = stickyHeaderContainerRef.current;

      if (!stickyHeader) {
        setIsTableHeaderStuck(false);
        return;
      }

      const { top } = stickyHeader.getBoundingClientRect();
      setIsTableHeaderStuck(top <= stickyHeaderTop + 1);
    };

    updateStickyHeaderState();
    window.addEventListener('scroll', updateStickyHeaderState, { passive: true });
    window.addEventListener('resize', updateStickyHeaderState);
    window.addEventListener('orientationchange', updateStickyHeaderState);

    return () => {
      window.removeEventListener('scroll', updateStickyHeaderState);
      window.removeEventListener('resize', updateStickyHeaderState);
      window.removeEventListener('orientationchange', updateStickyHeaderState);
    };
  }, [errorMessage, loading, rows.length, stickyHeaderTop]);

  const handleSearch = () => {
    loadTokenCosts(selectedUserId, selectedModelType);
  };

  const handleTableBodyScroll = (event) => {
    if (!tableHeaderViewportRef.current) {
      return;
    }

    tableHeaderViewportRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  const showPricingBlock = selectedModelType !== ANY_MODEL_VALUE && selectedModelPricing;
  const appCount = useMemo(() => new Set(rows.map((row) => row.app_id)).size, [rows]);
  const columnWidths = useMemo(() => ([
    appNameColumnWidth,
    220,
    120,
    170,
    140,
    170,
    140,
    140,
    170
  ]), [appNameColumnWidth]);
  const tableMinWidth = useMemo(() => columnWidths.reduce((sum, width) => sum + width, 0), [columnWidths]);

  const totals = useMemo(() => rows.reduce((accumulator, row) => ({
    creditsUsed: accumulator.creditsUsed + Number(row.credits_used || 0),
    totalInputTokens: accumulator.totalInputTokens + Number(row.total_input_tokens || 0),
    totalOutputTokens: accumulator.totalOutputTokens + Number(row.total_output_tokens || 0),
    inputCost: accumulator.inputCost + Number(row.input_cost || 0),
    outputCost: accumulator.outputCost + Number(row.output_cost || 0),
    totalCost: accumulator.totalCost + Number(row.total_cost || 0)
  }), {
    creditsUsed: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    inputCost: 0,
    outputCost: 0,
    totalCost: 0
  }), [rows]);

  const renderColumnGroup = () => (
    <colgroup>
      {columnWidths.map((width, index) => (
        <col key={index} style={{ width, minWidth: width, maxWidth: width }} />
      ))}
    </colgroup>
  );

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={{ minHeight: '100vh', background: pageBg, color: pageText }}>
      <AdminBar bgColor={headerBg} textColor={headerText} />
      <div style={{ padding: '60px 24px 24px', maxWidth: 1280, margin: '0 auto' }}>
        <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Token cost</h1>
        <p style={{ marginTop: 10, opacity: 0.82, maxWidth: 840 }}>
          View per-app token usage totals and cost rollups using the model pricing stored in the models table.
        </p>

        <div style={{ marginTop: 20, padding: 20, borderRadius: 16, background: cardBg, border: `1px solid ${borderColor}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'end' }}>
            <label style={{ display: 'grid', gap: 8, flex: `0 1 ${controlWidth}px`, width: '100%', maxWidth: controlWidth }}>
              <span style={{ fontWeight: 600 }}>User</span>
              <select
                value={selectedUserId}
                onChange={(event) => setSelectedUserId(event.target.value)}
                style={{ padding: '12px 14px', borderRadius: 10, border: `1px solid ${borderColor}`, background: inputBg, color: pageText }}
              >
                <option value={ANY_USER_VALUE}>Any user</option>
                {users.map((user) => {
                  const label = user.username || `User ${user.user_id}`;

                  return (
                    <option key={user.user_id} value={String(user.user_id)}>{label}</option>
                  );
                })}
              </select>
            </label>

            <label style={{ display: 'grid', gap: 8, flex: `0 1 ${controlWidth}px`, width: '100%', maxWidth: controlWidth }}>
              <span style={{ fontWeight: 600 }}>Model type</span>
              <select
                value={selectedModelType}
                onChange={(event) => setSelectedModelType(event.target.value)}
                style={{ padding: '12px 14px', borderRadius: 10, border: `1px solid ${borderColor}`, background: inputBg, color: pageText }}
              >
                <option value={ANY_MODEL_VALUE}>Any model</option>
                {models.map((model) => (
                  <option key={model.model_type} value={model.model_type}>{model.model_type}</option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={handleSearch}
              style={{
                flex: '0 0 220px',
                width: '100%',
                maxWidth: 220,
                padding: '12px 18px',
                borderRadius: 10,
                border: 'none',
                cursor: 'pointer',
                background: headerBg,
                color: '#ffffff',
                fontWeight: 600,
                minHeight: 46
              }}
            >
              Search
            </button>
          </div>
        </div>

        {showPricingBlock && (
          <div style={{ marginTop: 20, padding: 20, borderRadius: 16, background: cardBg, border: `1px solid ${borderColor}` }}>
            <h2 style={{ margin: 0, fontSize: '1.15rem' }}>Selected model pricing</h2>
            <p style={{ margin: '8px 0 0', opacity: 0.82 }}>
              {selectedModelPricing.model_type}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 16 }}>
              <div>
                <div style={{ opacity: 0.72 }}>Input tokens</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{formatCurrency(selectedModelPricing.input_cost_per_million)} per {formatInteger(selectedModelPricing.pricing_unit_tokens)}</div>
              </div>
              <div>
                <div style={{ opacity: 0.72 }}>Output tokens</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{formatCurrency(selectedModelPricing.output_cost_per_million)} per {formatInteger(selectedModelPricing.pricing_unit_tokens)}</div>
              </div>
              <div>
                <div style={{ opacity: 0.72 }}>Cached input tokens</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{formatCurrency(selectedModelPricing.cached_input_cost_per_million)} per {formatInteger(selectedModelPricing.pricing_unit_tokens)}</div>
              </div>
            </div>
          </div>
        )}

        {loading && <p style={{ marginTop: 20 }}>Loading token cost data...</p>}
        {!loading && errorMessage && <p style={{ marginTop: 20 }}>{errorMessage}</p>}

        {!loading && !errorMessage && (
          <div style={{ marginTop: 20, borderRadius: 16, background: cardBg, border: `1px solid ${borderColor}` }}>
            <div
              ref={stickyHeaderContainerRef}
              style={{
                position: 'sticky',
                top: stickyHeaderTop,
                zIndex: 6,
                background: stickyHeaderBg,
                borderTopLeftRadius: isTableHeaderStuck ? 0 : 16,
                borderTopRightRadius: isTableHeaderStuck ? 0 : 16,
                boxShadow: isTableHeaderStuck ? `0 -1px 0 0 ${borderColor}, -1px 0 0 0 ${borderColor}, 1px 0 0 0 ${borderColor}` : 'none',
                borderBottom: `1px solid ${borderColor}`
              }}
            >
              <div
                ref={tableHeaderViewportRef}
                style={{
                  overflow: 'hidden',
                  borderTopLeftRadius: isTableHeaderStuck ? 0 : 16,
                  borderTopRightRadius: isTableHeaderStuck ? 0 : 16
                }}
              >
                <table style={{ width: '100%', minWidth: tableMinWidth, borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                  {renderColumnGroup()}
                  <thead>
                    <tr style={{ background: stickyHeaderBg }}>
                      <th style={{ textAlign: 'left', padding: headerCellPadding, lineHeight: 1.2, verticalAlign: 'middle', width: appNameColumnWidth, whiteSpace: 'normal' }}>App name</th>
                      <th style={{ textAlign: 'left', padding: headerCellPadding, lineHeight: 1.2, verticalAlign: 'middle' }}>Model name</th>
                      <th style={{ textAlign: 'right', padding: headerCellPadding, lineHeight: 1.2, verticalAlign: 'middle' }}>Credits used</th>
                      <th style={{ textAlign: 'right', padding: headerCellPadding, lineHeight: 1.2, verticalAlign: 'middle' }}>Total input tokens</th>
                      <th style={{ textAlign: 'right', padding: headerCellPadding, lineHeight: 1.2, verticalAlign: 'middle' }}>Input cost</th>
                      <th style={{ textAlign: 'right', padding: headerCellPadding, lineHeight: 1.2, verticalAlign: 'middle' }}>Total output tokens</th>
                      <th style={{ textAlign: 'right', padding: headerCellPadding, lineHeight: 1.2, verticalAlign: 'middle' }}>Output cost</th>
                      <th style={{ textAlign: 'right', padding: headerCellPadding, lineHeight: 1.2, verticalAlign: 'middle' }}>Total cost</th>
                      <th style={{ textAlign: 'right', padding: headerCellPadding, lineHeight: 1.2, verticalAlign: 'middle' }}>Average cost per chat</th>
                    </tr>
                  </thead>
                </table>
              </div>
            </div>
            <div ref={tableBodyViewportRef} onScroll={handleTableBodyScroll} style={{ overflowX: 'auto', overflowY: 'visible', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
              <table style={{ width: '100%', minWidth: tableMinWidth, borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
                {renderColumnGroup()}
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.app_id}-${row.model_name}`}>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${borderColor}`, width: appNameColumnWidth, minWidth: appNameColumnWidth, maxWidth: appNameColumnWidth, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{row.app_name}</td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${borderColor}` }}>{row.model_name}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderBottom: `1px solid ${borderColor}` }}>{formatInteger(row.credits_used)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderBottom: `1px solid ${borderColor}` }}>{formatInteger(row.total_input_tokens)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderBottom: `1px solid ${borderColor}` }}>{formatCurrency(row.input_cost)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderBottom: `1px solid ${borderColor}` }}>{formatInteger(row.total_output_tokens)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderBottom: `1px solid ${borderColor}` }}>{formatCurrency(row.output_cost)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderBottom: `1px solid ${borderColor}`, fontWeight: 700 }}>{formatCurrency(row.total_cost)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderBottom: `1px solid ${borderColor}` }}>{formatCurrency(row.average_cost_per_chat)}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan="9" style={{ padding: '24px 16px', textAlign: 'center' }}>
                        No token cost data found for apps with a model type.
                      </td>
                    </tr>
                  )}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr style={{ background: isDarkMode ? '#181818' : '#f3f7fc' }}>
                      <td style={{ padding: '14px 16px', borderTop: `1px solid ${borderColor}`, fontWeight: 700 }}>Totals</td>
                      <td style={{ padding: '14px 16px', borderTop: `1px solid ${borderColor}`, opacity: 0.82 }}>{`${formatInteger(appCount)} app${appCount === 1 ? '' : 's'}`}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderTop: `1px solid ${borderColor}`, fontWeight: 700 }}>{formatInteger(totals.creditsUsed)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderTop: `1px solid ${borderColor}`, fontWeight: 700 }}>{formatInteger(totals.totalInputTokens)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderTop: `1px solid ${borderColor}`, fontWeight: 700 }}>{formatCurrency(totals.inputCost)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderTop: `1px solid ${borderColor}`, fontWeight: 700 }}>{formatInteger(totals.totalOutputTokens)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderTop: `1px solid ${borderColor}`, fontWeight: 700 }}>{formatCurrency(totals.outputCost)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderTop: `1px solid ${borderColor}`, fontWeight: 700 }}>{formatCurrency(totals.totalCost)}</td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', borderTop: `1px solid ${borderColor}`, fontWeight: 700, color: '#4582c6' }}>{totals.creditsUsed > 0 ? formatCurrency(totals.totalCost / totals.creditsUsed) : '$0.000000'}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}